import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ReplaySubject } from 'rxjs';
import { filter } from 'rxjs/operators';

export interface BreadcrumbEntry {
  label: string;
  url: string;
}

/**
 * BreadcrumbService is responsible for generating breadcrumb data for use in breadcrumb navigation components.
 * It listens to router events and updates the breadcrumbs based on the current route.
 *
 * @Injectable
 */
@Injectable({
  providedIn: 'root'
})
export class BreadcrumbService {
  public readonly $breadcrumbs = new ReplaySubject<Array<BreadcrumbEntry>>(1);

  /**
   * Constructor for the BreadcrumbComponent.
   *
   * @param {Router} router - The Angular router used for navigation.
   * @param {ActivatedRoute} activatedRoute - The current active route in the router.
   * @return {BreadcrumbComponent} A new instance of the BreadcrumbComponent.
   */
  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {
    const breadcrumbs = this.createBreadcrumbs(this.activatedRoute.root);
    this.$breadcrumbs.next(breadcrumbs);

    this.router.events.pipe(filter(event => event instanceof NavigationEnd)).subscribe(() => {
      const breadcrumbs = this.createBreadcrumbs(this.activatedRoute.root);
      this.$breadcrumbs.next(breadcrumbs);
    });
  }

  /**
   * Manually update breadcrumbs
   */
  public updateBreadcrumbs() {
    const breadcrumbs = this.createBreadcrumbs(this.activatedRoute.root);
    this.$breadcrumbs.next(breadcrumbs);
  }

  /**
   * Recursively creates a list of breadcrumb objects based on the activated route.
   *
   * @param {ActivatedRoute} route - The currently active route from which breadcrumbs are being generated.
   * @param {string} [url=''] - The accumulated URL path up to this route; defaults to an empty string.
   * @param {Array<{ label: string, url: string }>} [breadcrumbs=[]] - The collection of breadcrumb objects; each object contains a label and a URL.
   * @return {Array<{ label: string, url: string }>} An array of objects representing the breadcrumb trail, where each object has a `label` for display and a `url` for navigation.
   */
  private createBreadcrumbs(
    route: ActivatedRoute,
    url: string = '',
    breadcrumbs: Array<BreadcrumbEntry> = []
  ): Array<BreadcrumbEntry> {
    const children: ActivatedRoute[] = route.children;

    if (children.length === 0) {
      return breadcrumbs;
    }

    for (const child of children) {
      if (child.snapshot && child.snapshot.url) {
        const routeURL: string = child.snapshot.url.map(segment => segment.path).join('/');
        if (routeURL !== '') {
          url += `/${routeURL}`;
        }
      }

      const label = child.snapshot?.data?.['breadcrumb'];
      if (label) {
        breadcrumbs.push({ label: label, url: url });
      }

      return this.createBreadcrumbs(child, url, breadcrumbs);
    }

    return breadcrumbs;
  }
}
